import { useState } from "react";
import { Button, Input, Modal, Space, Steps, Typography, Alert } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../providers/dataProvider";
import { BackupCodesModal } from "./BackupCodesModal";

const { Text, Paragraph } = Typography;

interface MfaSetupModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export const MfaSetupModal: React.FC<MfaSetupModalProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [qrCodeUri, setQrCodeUri] = useState("");
  const [manualEntryKey, setManualEntryKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  const handleOpen = async () => {
    if (loading) return;
    try {
      setLoading(true);
      setError(null);
      const { data } = await axiosInstance.post("auth/mfa/setup");
      setQrCodeUri(data.qrCodeUri);
      setManualEntryKey(data.manualEntryKey);
      setCurrentStep(1);
    } catch {
      setError(t("auth.serverError"));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (code: string) => {
    if (loading) return;
    try {
      setLoading(true);
      setError(null);
      const { data } = await axiosInstance.post("auth/mfa/setup/confirm", {
        code,
      });
      if (data.success) {
        setBackupCodes(data.backupCodes);
        setShowBackupCodes(true);
        setCurrentStep(2);
      } else {
        setError(t("auth.mfa.invalidCode"));
      }
    } catch {
      setError(t("auth.mfa.invalidCode"));
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodesClose = () => {
    setShowBackupCodes(false);
    // Clear MFA enrollment required flag
    localStorage.removeItem("mfa_enrollment_required");
    onComplete();
    handleReset();
  };

  const handleReset = () => {
    setCurrentStep(0);
    setQrCodeUri("");
    setManualEntryKey("");
    setError(null);
    setBackupCodes([]);
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  return (
    <>
      <Modal
        title={t("auth.mfa.setupTitle")}
        open={open && !showBackupCodes}
        onCancel={handleCancel}
        footer={null}
        maskClosable={false}
        width={480}
        destroyOnClose
      >
        <Steps
          current={currentStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={[
            { title: t("buttons.create") },
            { title: t("auth.mfa.scanQrCode") },
            { title: t("auth.mfa.verify") },
          ]}
        />

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {currentStep === 0 && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Paragraph>{t("auth.mfa.scanQrCode")}</Paragraph>
            <Button
              type="primary"
              block
              size="large"
              loading={loading}
              onClick={handleOpen}
            >
              {t("buttons.create")}
            </Button>
          </Space>
        )}

        {currentStep === 1 && (
          <Space
            direction="vertical"
            align="center"
            style={{ width: "100%" }}
            size="middle"
          >
            <div
              style={{
                background: "#ffffff",
                padding: 12,
                borderRadius: 8,
                display: "inline-block",
              }}
            >
              <QRCodeSVG
                value={qrCodeUri}
                size={200}
                level="M"
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>

            <div style={{ textAlign: "center" }}>
              <Text type="secondary">{t("auth.mfa.manualEntry")}</Text>
              <div
                style={{
                  marginTop: 8,
                  padding: "8px 16px",
                  background: "rgba(0, 0, 0, 0.06)",
                  borderRadius: 6,
                  fontFamily: "monospace",
                  fontSize: 14,
                  wordBreak: "break-all",
                  userSelect: "all",
                  color: "rgba(0, 0, 0, 0.88)",
                }}
              >
                {manualEntryKey}
              </div>
            </div>

            <div style={{ width: "100%", textAlign: "center" }}>
              <Text type="secondary">{t("auth.mfa.confirmSetup")}</Text>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 12,
                }}
              >
                <Input.OTP
                  length={6}
                  onChange={(value) => {
                    if (value.length === 6) handleConfirm(value);
                  }}
                  disabled={loading}
                  size="large"
                />
              </div>
            </div>
          </Space>
        )}
      </Modal>

      <BackupCodesModal
        open={showBackupCodes}
        codes={backupCodes}
        onClose={handleBackupCodesClose}
      />
    </>
  );
};
